/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CustomAxiosError,
  DateString,
  Timestamp,
  TimeString,
  RecipientGroup,
  BlockedTimeSlot,
  CalendarEvent,
  User,
  MunuUserData,
  MonthFilterValue,
  NotificationType,
  FromToDateFilterValue,
} from "@/types";
import type { StatusError } from "@/api/client";
import { type QueryClient } from "@tanstack/react-query";
import { SortingFn } from "@tanstack/react-table";
import { AxiosError } from "axios";
import {
  addMinutes,
  isAfter,
  isBefore,
  isValid,
  subMinutes,
  isToday,
  isTomorrow,
  isSameYear,
  isDate,
  isSameDay,
  subHours,
} from "date-fns";
import { format } from "date-fns-tz";
import _ from "lodash";
import { Event } from "react-big-calendar";
import { Table as ReactTable, FilterFn } from "@tanstack/react-table";
import { NO_VALUE_MARKER, HAS_VALUE_MARKER } from "./constants";
import React, { CSSProperties, ReactNode } from "react";

export const getBuildingId = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const buildingId = searchParams.get("building");
  return buildingId || "";
};

export {
  clearStoredCompanies,
  clearStoredCompany,
} from "@/store/storedCompany";

export const hasCustomErrorCode = (
  error: unknown,
): error is CustomAxiosError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error && // Axios-specific flag
    (error as AxiosError<CustomAxiosError>).response?.data?.code !== undefined // Ensure `code` exists
  );
};

/**
 * Status and backend error code from a failed request. Works for raw axios
 * errors and for ones rewrapped by `throwCaughtError`.
 */
export const getApiErrorDetails = (
  error: unknown,
): { status?: number; code?: string; data?: unknown } => {
  const raw = error as {
    response?: { status?: number; data?: { code?: string; data?: unknown } };
  };
  const wrapped = error as StatusError;
  const body = raw?.response?.data ?? wrapped?.data;
  return {
    status: raw?.response?.status ?? wrapped?.status,
    code: body?.code,
    data: body?.data,
  };
};

type AddNotification = (
  message: string | ReactNode,
  type?: NotificationType,
  persistent?: boolean,
  onClose?: () => void,
) => number;

type TranslateFn = (key: any, properties?: any) => any;

/**
 * Backend error codes are only useful if `NOTIFICATIONS.<code>` is translated.
 * i18next returns the key itself when it isn't, which would surface a raw
 * "NOTIFICATIONS.SOME_CODE" to the user — worse than a generic message.
 */
export const translateErrorCode = (
  t: TranslateFn,
  code: string,
  properties?: any,
): string | null => {
  const key = `NOTIFICATIONS.${code}`;
  const translated = t(key, properties);
  return translated === key ? null : translated;
};

const ENTITY_PROBE = "__entity__";

/**
 * True when a message interpolates {{entity}}. CREATE_FAILED and friends do, and
 * i18next renders a missing value as "", producing "Failed to update ." — so a
 * caller that forgets `entity` should get a generic message instead of a
 * sentence with a hole in it.
 */
const interpolatesEntity = (t: TranslateFn, fallbackKey: string): boolean =>
  String(t(`NOTIFICATIONS.${fallbackKey}`, { entity: ENTITY_PROBE })).includes(
    ENTITY_PROBE,
  );

type NotifyApiErrorOptions = {
  /** Interpolation values, applied to both the code message and the fallback. */
  properties?: any;
  /** Used when the error carries no code, or the code has no translation. */
  fallbackKey?: string;
  /** Severity of the fallback notification. Defaults to ERROR. */
  fallbackType?: NotificationType;
};

/**
 * Readable message for an API error. Prefers the backend's specific error code,
 * and always returns something, so a failure is never described as nothing.
 */
export const describeApiError = (
  error: unknown,
  t: TranslateFn,
  { properties, fallbackKey = "SERVER_ERROR" }: NotifyApiErrorOptions = {},
): { message: string; usedCode: boolean } => {
  if (hasCustomErrorCode(error) && error.response) {
    const { code } = error.response.data;
    // Same guard as the fallback below: a code message needing an entity the
    // caller didn't give would show "{{entity}}", so it isn't used
    const message =
      properties?.entity || !interpolatesEntity(t, code)
        ? translateErrorCode(t, code, properties)
        : null;
    if (message) return { message, usedCode: true };
  }
  const key =
    !properties?.entity && interpolatesEntity(t, fallbackKey)
      ? "SERVER_ERROR"
      : fallbackKey;

  return {
    message: t(`NOTIFICATIONS.${key}`, properties),
    usedCode: false,
  };
};

/**
 * Surfaces an API error to the user. Prefers the backend's specific error code,
 * and always falls back to something readable so a failed action is never silent.
 */
export const notifyApiError = (
  error: unknown,
  addNotification: AddNotification,
  t: TranslateFn,
  options: NotifyApiErrorOptions = {},
) => {
  const { message, usedCode } = describeApiError(error, t, options);
  addNotification(
    message,
    usedCode
      ? NotificationType.ERROR
      : (options.fallbackType ?? NotificationType.ERROR),
  );
};

/**
 * Appends the offending file names to an error message, so a user with several
 * images selected can tell which ones were rejected.
 */
export const notifyWithFileNames = (
  message: string,
  fileNames: string[] | undefined,
  t: TranslateFn,
): string =>
  fileNames?.length
    ? t("NOTIFICATIONS.WITH_FILES", {
        message,
        files: fileNames.join(", "),
      })
    : message;

export const notifyImageUploadError = (
  error: unknown,
  addNotification: AddNotification,
  t: TranslateFn,
  entity?: string,
) =>
  notifyApiError(error, addNotification, t, {
    properties: { entity: entity ?? t("IMAGE.LABEL") },
    fallbackKey: "UPLOAD_IMAGE_FAILED",
    fallbackType: NotificationType.WARNING,
  });

export const NOK = Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

export const formatRoomPrice = ({
  fixedFee,
  hourlyRate,
  t,
}: {
  fixedFee?: number;
  hourlyRate?: number;
  t: (key: any, properties?: any) => any;
}) => {
  if (fixedFee && hourlyRate) {
    return `${NOK.format(fixedFee)} + ${NOK.format(hourlyRate)} / ${t("HOURS", { count: 1 }).toLowerCase()}`;
  } else if (fixedFee) {
    return `${NOK.format(fixedFee)} / ${t("BOOKINGS.BOOKING").toLowerCase()}`;
  } else if (hourlyRate) {
    return `${NOK.format(hourlyRate)} / ${t("HOURS", { count: 1 }).toLowerCase()}`;
  }
  return "";
};

// sv-SE formats the date as ISO YYYY-MM-DD
export const ZONED_ISO_DATE = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Oslo",
});

const NUMERIC_DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};
export const NUMERIC_DATE_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    NUMERIC_DATE_LOCALE_OPTIONS,
  );

const DATE_MONTH_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "numeric",
  day: "numeric",
};
export const DATE_MONTH_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    DATE_MONTH_LOCALE_OPTIONS,
  );

const FULL_DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};
export const FULL_DATE_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    FULL_DATE_LOCALE_OPTIONS,
  );

const DATE_DAY_MONTH_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeStyle: undefined,
};

export const DATE_DAY_MONTH_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    DATE_DAY_MONTH_OPTIONS,
  );

export const SIMPLE_TIME_LOCALE = new Intl.DateTimeFormat("nb-NO", {
  hour: "numeric",
  minute: "numeric",
});

const DATE_TIME_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

export const DATE_TIME_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    DATE_TIME_LOCALE_OPTIONS,
  );

const NUMERIC_DATE_TIME_LOCALE_OPTIONS = (
  withSecs?: boolean,
  withYear?: boolean,
): Intl.DateTimeFormatOptions => ({
  month: "numeric",
  day: "numeric",
  ...(withYear !== false ? { year: "numeric" } : {}), // Default to showing year unless explicitly set to false
  hour: "numeric",
  minute: "numeric",
  ...(withSecs ? { second: "numeric" } : {}), // Default to not showing seconds unless explicitly set to true
});

export const NUMERIC_DATE_TIME_LOCALE = (
  lang: string,
  withSecs?: boolean,
  withYear?: boolean,
) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    NUMERIC_DATE_TIME_LOCALE_OPTIONS(withSecs, withYear),
  );

const BOOKING_DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  weekday: "short",
};
export const BOOKING_DATE_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    BOOKING_DATE_LOCALE_OPTIONS,
  );

const FULL_DATE_TIME_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  weekday: "long",
  hour: "numeric",
  minute: "numeric",
};
export const FULL_DATE_TIME_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    FULL_DATE_TIME_LOCALE_OPTIONS,
  );

const DATE_WEEKDAY_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  weekday: "long",
};
export const DATE_WEEKDAY_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    DATE_WEEKDAY_LOCALE_OPTIONS,
  );

// Weekday and date without the year: "Friday 11 September" / "fredag 11. september".
// Used where the date is context, not a record (dashboard hero).
const HERO_DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
};
export const HERO_DATE_LOCALE = (lang: string) =>
  new Intl.DateTimeFormat(
    lang === "en" ? "en-GB" : "nb-NO",
    HERO_DATE_LOCALE_OPTIONS,
  );

export const firebaseTimestampToDate = (
  timestamp: Timestamp | string | number | Date,
): Date => {
  /* Handle other types in case the type of the timestamp is incorrect */
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp._seconds * 1000);
};

export const isValidFirebaseTimestamp = (timestamp: Timestamp): boolean => {
  return (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "_seconds" in timestamp &&
    "_nanoseconds" in timestamp &&
    isValid(new Date(timestamp._seconds * 1000))
  );
};

export const dateToFirebaseTimestamp = (date: Date): Timestamp => {
  const time = date.getTime();
  return {
    _seconds: Math.floor(time / 1000),
    _nanoseconds: (time % 1000) * 1e6,
  };
};

export const monthFilterValueToDate = (monthString: MonthFilterValue): Date => {
  const [year, month] = monthString.split("-");
  const yearNumber = Number(year);
  const monthNumber = Number(month) - 1;
  return new Date(yearNumber, monthNumber, 1);
};

export const dateToMonthFilterValue = (date: Date): MonthFilterValue => {
  const year = date.getFullYear();
  const paddedMonth = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${paddedMonth}` as MonthFilterValue;
};

export const formatTimestampToTimeAgo = (
  t: (key: any, values?: any) => any,
  timestamp: Timestamp,
  lang: string = "no",
  withDate?: boolean,
): string => {
  if (!timestamp._seconds) {
    return "Invalid timestamp";
  }

  const currTime = new Date();
  const date = firebaseTimestampToDate(timestamp);
  const diffInMinutes = Math.floor(
    (currTime.getTime() - date.getTime()) / 60000,
  );

  if (diffInMinutes <= 0) {
    return t("JUST_NOW");
  } else if (diffInMinutes < 60) {
    return t("MINUTES_AGO", { count: diffInMinutes });
  } else if (withDate) {
    if (currTime.getFullYear() !== date.getFullYear()) {
      return FULL_DATE_LOCALE(lang).format(date);
    } else if (isToday(date)) {
      return `${t("TODAY")}, ${SIMPLE_TIME_LOCALE.format(date)}`;
    } else {
      return NUMERIC_DATE_TIME_LOCALE(lang, false, false).format(date);
    }
  } else {
    return SIMPLE_TIME_LOCALE.format(date);
  }
};

export const dateSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId) as string | Date | number | undefined;
  const bValue = rowB.getValue(columnId) as string | Date | number | undefined;

  const aTime =
    typeof aValue === "number"
      ? aValue
      : isDate(aValue)
        ? aValue.getTime()
        : aValue
          ? new Date(aValue).getTime()
          : 0;
  const bTime =
    typeof bValue === "number"
      ? bValue
      : isDate(bValue)
        ? bValue.getTime()
        : bValue
          ? new Date(bValue).getTime()
          : 0;

  return aTime - bTime;
};

export const timestampSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId) as Timestamp | undefined;
  const bValue = rowB.getValue(columnId) as Timestamp | undefined;

  const aSeconds = aValue?._seconds ?? 0;
  const bSeconds = bValue?._seconds ?? 0;

  return aSeconds - bSeconds;
};

export const timestampMultiColumnSortingFn =
  (columnIds: string[]): SortingFn<any> =>
  (rowA, rowB) => {
    const getFirstValue = (row: any) => {
      for (const id of columnIds) {
        const ts = row.original[id] as Timestamp | undefined;
        if (ts?._seconds != null) {
          return ts._seconds;
        }
      }
      return 0; // All empty
    };

    const aSeconds = getFirstValue(rowA);
    const bSeconds = getFirstValue(rowB);

    return aSeconds - bSeconds;
  };

export const longNumberStringSortingFn: SortingFn<any> = (
  rowA,
  rowB,
  columnId,
) => {
  const parseValue = (value: any): bigint => {
    if (value === "" || value == null || value === undefined) return 0n; // Treat empty/null/undefined as 0
    try {
      return BigInt(value);
    } catch {
      return 0n; // Fallback for invalid values
    }
  };

  const aValue = parseValue(rowA.getValue(columnId));
  const bValue = parseValue(rowB.getValue(columnId));

  return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
};

export const arrayLengthSortingFn = (
  rowA: any,
  rowB: any,
  columnId: string,
) => {
  const aValue = rowA.getValue(columnId) as any[];
  const bValue = rowB.getValue(columnId) as any[];

  return (aValue?.length ?? 0) - (bValue?.length ?? 0);
};

export const dateLikeSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  const getSeconds = (value: any): number => {
    if (!value) return 0;

    if (value._seconds != null) {
      return value._seconds; // real Firebase Timestamp
    }

    if (typeof value === "string") {
      const seconds = Math.floor(new Date(value).getTime() / 1000);
      return isNaN(seconds) ? 0 : seconds;
    }

    return 0;
  };

  return getSeconds(a) - getSeconds(b);
};

export const computedNumberSortingFn = (
  valueGetter: (row: any) => number,
): SortingFn<any> => {
  return (rowA: any, rowB: any) => {
    const a = valueGetter(rowA.original);
    const b = valueGetter(rowB.original);
    return a - b;
  };
};

export const objectValueFilterFn: FilterFn<any> = (
  row: any,
  columnId: string,
  filterValue: string[],
) => {
  const rowValue = row.getValue(columnId);

  if (filterValue.includes("none") && !rowValue) return true;

  if (
    typeof rowValue === "object" &&
    rowValue !== null &&
    !Array.isArray(rowValue)
  ) {
    return filterValue.some((key) => rowValue[key]);
  }

  return false;
};

export const multiStringIncludesFilterFn: FilterFn<any> = (
  row: any,
  columnId: string,
  filterValue: string[],
) => {
  const rowValue = row.getValue(columnId);

  // Handle NO_VALUE_MARKER (filtering to find missing values) - match both null, undefined and empty string
  if (filterValue.includes(NO_VALUE_MARKER)) {
    if (rowValue == null || rowValue === "") return true;
  }
  // If not filtering for missing values - return false early if value is null, undefined or empty string
  else if (rowValue == null || rowValue === "") return false;

  const rowValueStr = String(rowValue).toLowerCase();
  return filterValue.some((val) => {
    if (val === NO_VALUE_MARKER) return false; // Already handled above
    return rowValueStr.includes(String(val).toLowerCase());
  });
};

export const arrayIncludesFilterFn: FilterFn<any> = (
  row: any,
  columnId: string,
  filterValue: any[],
) => {
  const rowValue = row.getValue(columnId);

  // Handle NO_VALUE_MARKER - match both null, undefined and empty string
  if (
    filterValue.includes(NO_VALUE_MARKER) &&
    (rowValue == null || rowValue === "")
  ) {
    return true;
  }

  // Check if the row value matches any of the filter values
  return filterValue.some((val) => {
    if (val === NO_VALUE_MARKER) return false; // Already handled above
    return val === rowValue;
  });
};

export const forcedBooleanFilterFn: FilterFn<any> = (
  row: any,
  columnId: string,
  filterValue: any[],
) => {
  const rowValue = row.getValue(columnId);

  // Check if NO_VALUE_MARKER is selected - matches null, undefined, empty string, and false
  if (filterValue.includes(NO_VALUE_MARKER)) {
    if (rowValue == null || rowValue === "" || rowValue === false) {
      return true;
    }
  }

  // Check if HAS_VALUE_MARKER is selected - matches any other value
  if (filterValue.includes(HAS_VALUE_MARKER)) {
    if (!(rowValue == null || rowValue === "" || rowValue === false)) {
      return true;
    }
  }

  return false;
};

export type DateFilterValue = {
  start?: Date | null;
  end?: Date | null;
  weekdays?: number[]; // 0 = Sunday, 1 = Monday, ...
};

export const dateFilterFn: FilterFn<any> = (
  row,
  columnId,
  filterValue: DateFilterValue,
) => {
  const rawValue = row.getValue(columnId);
  if (!rawValue) return false;

  // Handle array values (e.g., [lastLogin, lastActive]) by extracting the first element
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;

  if (!value) return false;

  let date: Date;

  if (isValidFirebaseTimestamp(value as any)) {
    date = firebaseTimestampToDate(value as any);
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  } else {
    return false;
  }

  if (!isValid(date)) return false;

  const { start, end, weekdays } = filterValue;

  // 1. Check Weekday
  // getDay() returns 0 for Sunday, 1 for Monday...
  if (weekdays && weekdays.length > 0) {
    const day = date.getDay();
    if (!weekdays.includes(day)) {
      return false;
    }
  }

  // 2. Check Date Range
  if (start || end) {
    if (start && !end) {
      // If only start is selected, filter for that specific day
      return isSameDay(date, start);
    } else {
      // If start and end (or just end?), treat as range
      // Assume inclusive boundaries
      if (start && isBefore(date, start)) {
        return false;
      }
      if (end && isAfter(date, end)) {
        return false;
      }
    }
  }

  return true;
};

type NormalizedCard =
  { type: "number"; value: bigint } | { type: "string"; value: string };

export const mixedCardSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const normalize = (value: any): NormalizedCard => {
    const str = String(value).trim();

    if (/^[0-9]+$/.test(str)) {
      return { type: "number", value: BigInt(str) };
    }

    return { type: "string", value: str.toLowerCase() };
  };

  const a = normalize(rowA.getValue(columnId));
  const b = normalize(rowB.getValue(columnId));

  if (a.type === "number" && b.type === "number") {
    return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
  }

  if (a.type === "number" && b.type === "string") return -1;
  if (a.type === "string" && b.type === "number") return 1;

  return String(a.value).localeCompare(String(b.value));
};

export const stripHtml = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;

  // Add a space between block-level elements to avoid word concatenation
  const blockTags = new Set(["P", "DIV", "BR", "LI", "UL", "OL"]);

  function getTextWithSpacing(node: Node) {
    let text = "";

    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        text += getTextWithSpacing(child);
        const element = child as Element;
        if (blockTags.has(element.tagName)) {
          text += " "; // Add a space after block elements
        }
      }
    });

    return text;
  }

  return getTextWithSpacing(div).replace(/\s+/g, " ").trim();
};

export const getCustomWelcomeMessageHTML = ({
  customWelcomeMessage,
  language,
  userName = "Ola",
  displayName,
  owner,
  buildingAdminName,
  jobTitle,
  companyName,
  signature,
}: {
  customWelcomeMessage?: { en: string; no: string };
  language: string;
  userName?: string;
  displayName: string;
  owner: string;
  buildingAdminName?: string;
  jobTitle?: string;
  companyName?: string;
  signature?: string;
}): string => {
  const safeJobTitle =
    jobTitle && companyName
      ? jobTitle
      : language === "en"
        ? "Property Manager"
        : "Eiendomssjef";
  const safeCompanyName = companyName && jobTitle ? companyName : owner;
  return customWelcomeMessage &&
    customWelcomeMessage.en &&
    customWelcomeMessage.no
    ? customWelcomeMessage[language === "en" ? "en" : "no"]
        // Replace all template variables
        .replaceAll("{{ userName }}", userName)
        .replaceAll("{{ buildingName }}", displayName)
        .replaceAll("{{ ownerName }}", owner)
        .replaceAll("{{ buildingAdminName }}", buildingAdminName ?? "")
        .replaceAll("{{ jobTitle }}", safeJobTitle)
        .replaceAll("{{ companyName }}", safeCompanyName)
        .replaceAll("{{ signature }}", signature ?? "")
        // Replace all custom HTML tags
        .replaceAll(
          "<regards>",
          `<div class="regards"${buildingAdminName ? "" : ' style="display: none;"'}>`,
        )
        .replaceAll("</regards>", "</div>")
        .replaceAll(
          "<signature>",
          `<p class="no-select signature"${buildingAdminName ? "" : ' style="display: none;"'}>`,
        )
        .replaceAll("</signature>", "</p>")
    : "";
};

export const validatePhoneNumber = (number: string) => {
  if (isNorwegianNumber(number)) {
    const cleanedNumber = cleanPhoneNumber(number);
    if (
      (cleanedNumber.includes("+47") && cleanedNumber.length === 11) ||
      (!cleanedNumber.includes("+47") && cleanedNumber.length === 8)
    ) {
      return true;
    } else {
      return false;
    }
  }

  if (number.length >= 8 && number.includes("+")) {
    return true;
  }

  return false;
};

export const cleanPhoneNumber = (number: string) => {
  if (!number) {
    return "";
  }
  return number.replace(/[^\d+]/g, "");
};

export const isNumberMissingCountryCode = (number: string) => {
  //this is only needed for foreign numbers. We can only assume that numbers with more than 8 digits are foreign.
  return number.length > 8 && !number.includes("+");
};

export const isNorwegianNumber = (number: string) => {
  return (
    number.substring(0, 3) === "+47" ||
    (!number.includes("+") && number.length == 8)
  );
};

export const addNorwegianCountryCodeToNorwegianNumbers = (number: string) => {
  if (isNorwegianNumber(number)) {
    if (!number.includes("+47")) {
      return "+47" + number;
    }
  }
  return number;
};

export const findMatchingCountryCode = (
  countryCodes: {
    code: string;
    dialCode: string;
  }[],
  phone?: string,
) => {
  if (!phone || phone.length < 4) return "";
  // Sort country codes by length (longest first)
  const sortedCountries = countryCodes.sort(
    (a, b) => b.dialCode.length - a.dialCode.length,
  );
  return (
    sortedCountries.find((c) => phone.startsWith(c.dialCode))?.dialCode || ""
  );
};

export const getPreviousFullHalfHour = (date?: Date): Date => {
  const result = date ? new Date(date) : new Date();
  const minutes = result.getMinutes();
  result.setMinutes(minutes < 30 ? 0 : 30, 0, 0);
  return result;
};

const isDateLikeField = (fieldName: string): boolean => {
  const lowerWhole = /(^|[^a-z])(date|time|at|on)(?=[^a-z]|$)/;
  const capitalSub = /(Date|Time|At|On)(?![a-z])/;
  return lowerWhole.test(fieldName) || capitalSub.test(fieldName);
};

const processExportValue = (
  fieldName: string,
  value: any,
  customValueHandlers?: { [header: string]: (value: any) => any },
): any => {
  try {
    if (customValueHandlers?.[fieldName]) {
      return customValueHandlers[fieldName](value) ?? "";
    }
    if (value == null) return "";
    if (isDateLikeField(fieldName)) {
      if (
        (typeof value === "string" || value instanceof Date) &&
        isValid(new Date(value))
      ) {
        return formatDateToTimezonedString(value);
      } else if (
        typeof value === "object" &&
        "_seconds" in value &&
        "_nanoseconds" in value
      ) {
        return formatDateToTimezonedString(
          firebaseTimestampToDate(value as Timestamp),
        );
      }
    }
    return value ?? "";
  } catch (error) {
    console.error(
      `Error processing value for field "${fieldName}":`,
      value,
      error,
    );
    return value ?? "";
  }
};

export const getCSVFromArray = (
  items: any[],
  headers?: string[],
  customValueHandlers?: { [header: string]: (value: any) => any },
): string => {
  const header = headers || (items.length > 0 ? Object.keys(items[0]) : []);

  const processValue = (fieldName: string, value: any): any =>
    processExportValue(fieldName, value, customValueHandlers);

  const escapeForCSV = (val: any): string => {
    let str =
      val instanceof Date
        ? formatDateToTimezonedString(val)
        : String(val ?? "");

    // Detect numbers
    const isNumeric = /^[-+]?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(str);

    if (!isNumeric && /^[=+\-@]/.test(str)) {
      // Prevent formula injection by prefixing with a single quote
      str = "'" + str;
    }

    // Escape CSV special chars
    if (/[",\r\n]/.test(str)) {
      str = `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  };

  return [
    header.map(escapeForCSV).join(","), // header row
    ...items.map((row) =>
      header
        .map((fieldName) =>
          escapeForCSV(processValue(fieldName, row[fieldName])),
        )
        .join(","),
    ),
  ].join("\r\n");
};

export const downloadArrayAsCSV = (
  items: any[],
  filename = "data",
  headers?: string[],
  customValueHandlers?: { [header: string]: (value: any) => any },
) => {
  const csv = getCSVFromArray(items, headers, customValueHandlers);
  downloadFile(new Blob([csv], { type: "text/csv" }), `${filename}.csv`);
};

type XlsxCell = { t: "s" | "n" | "b" | "d"; v: any; z?: string };

export const buildXlsxAoa = (
  items: any[],
  headers?: string[],
  customValueHandlers?: { [header: string]: (value: any) => any },
  cellFormatMap?: { [fieldName: string]: string },
): XlsxCell[][] => {
  const header = headers || (items.length > 0 ? Object.keys(items[0]) : []);

  const toCell = (fieldName: string, value: any): XlsxCell => {
    const fmt = cellFormatMap?.[fieldName];
    if (value == null || value === "") return { t: "s", v: "" };
    if (value instanceof Date)
      return { t: "d", v: value, ...(fmt && { z: fmt }) };
    if (typeof value === "number")
      return { t: "n", v: value, ...(fmt && { z: fmt }) };
    if (typeof value === "boolean") return { t: "b", v: value };
    // A YYYY-MM-DD string from a custom handler becomes a date cell so
    // Excel can format it — the plain string is preserved as-is in CSV.
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return { t: "d", v: new Date(value), ...(fmt && { z: fmt }) };
    }
    // Force all other strings to stay as text so Excel does not interpret them
    // as date serials or arithmetic.
    return { t: "s", v: String(value), ...(fmt && { z: fmt }) };
  };

  return [
    header.map((h) => ({ t: "s" as const, v: h })),
    ...items.map((row) =>
      header.map((fieldName) =>
        toCell(
          fieldName,
          processExportValue(fieldName, row[fieldName], customValueHandlers),
        ),
      ),
    ),
  ];
};

export const downloadArrayAsXlsx = async (
  items: any[],
  filename = "data",
  headers?: string[],
  customValueHandlers?: { [header: string]: (value: any) => any },
  cellFormatMap?: { [fieldName: string]: string },
) => {
  const XLSX = await import("xlsx");
  const aoa = buildXlsxAoa(items, headers, customValueHandlers, cellFormatMap);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const downloadBlobAsXlsx = async (
  blob: Blob,
  filename = "data.xlsx",
) => {
  const XLSX = await import("xlsx");
  const csvString = await blob.text();
  const workbook = XLSX.read(csvString, {
    type: "string",
    FS: ",",
  });
  XLSX.writeFile(workbook, filename);
};

export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const base64Data = base64.split(",")[1] || base64;
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

export const isViewableInBrowser = (filename: string): boolean => {
  const viewableExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "pdf",
    "txt",
    "svg",
  ];
  const extension = filename.split(".").pop()?.toLowerCase();
  return !!extension && viewableExtensions.includes(extension);
};

export const downloadFileFromUrl = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    downloadFile(blob, filename);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const capitalize = (s?: string) =>
  (s && String(s[0]).toUpperCase() + String(s).slice(1)) || "";

const SHORT_WEEKDAY_LOCALE = (lang: string) => {
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nb-NO", {
    weekday: "short",
  });
};
const LONG_WEEKDAY_LOCALE = (lang: string) => {
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nb-NO", {
    weekday: "long",
  });
};
export const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
export const getWeekdayFromNumber = (
  weekday: number,
  lang: string = "no",
  short = false,
) => {
  if (weekday < 0 || weekday > 6) return "Invalid weekday";
  return capitalize(
    (short ? SHORT_WEEKDAY_LOCALE(lang) : LONG_WEEKDAY_LOCALE(lang)).format(
      new Date(0, 0, weekday),
    ),
  );
};
export const formatOpenOnDays = (
  closedOnDays: number[],
  lang: string = "no",
  t: (key: any, values?: any) => any,
) => {
  if (closedOnDays.length === 7) {
    return "Invalid opening hours";
  }

  if (closedOnDays.length === 0) {
    return t("ALL_DAYS").toLowerCase();
  }

  if (
    closedOnDays.length === 2 &&
    closedOnDays.includes(0) &&
    closedOnDays.includes(6)
  ) {
    // Closed on weekends (return "Monday - Friday")
    return `${getWeekdayFromNumber(1, lang)} - ${getWeekdayFromNumber(5, lang)}`;
  }
  const openOnDays = ALL_WEEKDAYS.filter((day) => !closedOnDays.includes(day));

  // Order days from 1 (monday) to 6 (saturday), then 0 (sunday)
  openOnDays.sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));

  // Group consecutive days
  const groups: number[][] = [];
  let tempGroup: number[] = [openOnDays[0]];

  for (let i = 1; i < openOnDays.length; i++) {
    const prev = openOnDays[i - 1] === 0 ? 7 : openOnDays[i - 1]; // Treat Sunday (0) as 7
    const current = openOnDays[i] === 0 ? 7 : openOnDays[i];

    if (current === prev + 1) {
      tempGroup.push(openOnDays[i]);
    } else {
      groups.push(tempGroup);
      tempGroup = [openOnDays[i]];
    }
  }
  groups.push(tempGroup);

  // Format groups (only as ranges if 3+ days)
  const formattedDays = groups.map((group) => {
    if (group.length >= 3) {
      return `${getWeekdayFromNumber(group[0], lang)} - ${getWeekdayFromNumber(group[group.length - 1], lang)}`;
    } else {
      return group.map((day) => getWeekdayFromNumber(day, lang)).join(", ");
    }
  });

  // Replace last comma with " & " if there are multiple groups
  return formattedDays.length > 1
    ? formattedDays.slice(0, -1).join(", ") + " & " + formattedDays.at(-1)
    : formattedDays[0];
};

export const formatFloorNumber = (
  floor: string | number = 0,
  t: (key: any) => any,
): string => {
  const floorNum = Number(floor);
  if (floorNum < 0) {
    return `${t("FLOOR")} ${floor.toString().replace("-", "U")}`;
  } else {
    return `${floor}. ${t("FLOOR").toLowerCase()}`;
  }
};

export const formatTimeUntil = (
  rawDate: Timestamp | Date | string | number,
  t: (key: any, values?: any) => any,
  lang: string = "no",
) => {
  const date = firebaseTimestampToDate(rawDate);

  if (!isValid(date)) return "Invalid date";

  const timeString = date.toLocaleTimeString(
    lang === "en" ? "en-GB" : "nb-NO",
    { timeStyle: "short" },
  );

  if (isToday(date)) {
    return `${t("TODAY")}, ${t("AT")} ${timeString}`;
  } else if (isTomorrow(date)) {
    return `${t("TOMORROW")}, ${t("AT")} ${timeString}`;
  } else if (isSameYear(new Date(), date)) {
    return DATE_TIME_LOCALE(lang).format(date);
  } else {
    return FULL_DATE_LOCALE(lang).format(date);
  }
};

export const formatTimestampToHoursAndMinutes = (timestamp: Timestamp) => {
  if (!timestamp._seconds) {
    return "Invalid timestamp";
  }
  const date = firebaseTimestampToDate(timestamp);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const formatHoursToMinutesAndHours = (
  hours: number,
  t: (key: any, values?: any) => any,
  onlyHoursAboveOne?: boolean,
): string => {
  if (hours <= 0) return `0 ${t("MINUTES", { count: 2 }).toLowerCase()}`;
  else if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} ${t("MINUTES", { count: minutes }).toLowerCase()}`;
  } else if (!Number.isInteger(hours) && !onlyHoursAboveOne) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours} ${t("HOURS", { count: wholeHours }).toLowerCase()}, ${minutes} ${t("MINUTES", { count: minutes }).toLowerCase()}`;
  } else {
    return `${hours} ${t("HOURS", { count: Math.floor(hours) }).toLowerCase()}`;
  }
};

export const getPercentage = (x?: number, total?: number): number => {
  if (!total || typeof total !== "number" || total === 0) {
    return 0;
  }
  let percentage = Math.floor(((x ?? 0) / total) * 100);
  if (!isFinite(percentage) || isNaN(percentage)) percentage = 0;
  return percentage;
};

export const percentageSortingFn = (
  xAccessor?: string,
  totalAccessor?: string,
): SortingFn<any> => {
  return (rowA: any, rowB: any, columnId: string) => {
    // Helper function to extract totalUsers with a fallback for invalid data
    const getSortableTriple = (row: any): [number, number, number] => {
      if (xAccessor && totalAccessor) {
        let x = _.get(row.original, xAccessor) ?? 0;
        let total = _.get(row.original, totalAccessor) ?? 0;
        if (Array.isArray(x)) {
          x = x.length;
        }
        if (typeof x === "string") {
          x = parseInt(x, 10);
        }
        if (typeof x !== "number") {
          x = 0;
        }
        if (Array.isArray(total)) {
          total = total.length;
        }
        if (typeof total === "string") {
          total = parseInt(total, 10);
        }
        if (typeof total !== "number") {
          total = 0;
        }
        return [1, getPercentage(x, total), total]; // [validity flag, percentage value, total value]
      }
      const value = row.getValue(columnId);
      if (Array.isArray(value) && typeof value[1] === "number") {
        const [x, total] = value;
        return [1, getPercentage(x, total), total ?? 0];
      }
      return [0, 0, 0]; // Invalid data: [invalid flag, zero priority value, zero total value]
    };

    const [aValidFlag, percentageA, totalA] = getSortableTriple(rowA);
    const [bValidFlag, percentageB, totalB] = getSortableTriple(rowB);

    // Sort by validity first
    if (aValidFlag !== bValidFlag) {
      return bValidFlag - aValidFlag; // Invalid (0) comes before valid (1)
    }

    // Sort by percentage second
    if (percentageA !== percentageB) {
      return percentageA - percentageB;
    }

    // Sort by total third if all else is equal
    return totalA - totalB;
  };
};

export const createFoodActiveSortingFn = (
  queryClient: QueryClient,
  hasMunu?: boolean,
) => {
  return (rowA: any, rowB: any, columnId: string) => {
    // Helper function to get the actual cell value after query completion
    const getCellValue = (row: any): number => {
      const companyId = row.getValue(columnId) as string;

      // Get cached data from React Query
      const cachedData = queryClient.getQueryData([
        hasMunu ? "munu-company-overview" : "food-company",
        companyId,
      ]);

      if (!cachedData) return 0; // No data loaded yet, treat as 0

      const { activeUsers, integratedUsersCount } = (cachedData || {}) as {
        activeUsers?: number | null;
        integratedUsersCount?: number | null;
      };

      const usersValue = activeUsers || integratedUsersCount;

      // Convert to number, treating undefined & null as 0
      if (usersValue === undefined || usersValue === null) {
        return 0;
      }

      const numValue = Number(usersValue);
      return isNaN(numValue) ? 0 : numValue;
    };

    const aValue = getCellValue(rowA);
    const bValue = getCellValue(rowB);

    return aValue - bValue;
  };
};

export const createFoodPercentageSortingFn = (
  queryClient: QueryClient,
  hasMunu: boolean,
) => {
  return (rowA: any, rowB: any) => {
    const getSortableTriple = (row: any): [number, number, number] => {
      const total = (row.original.foodMaxIntegratedUsers ?? 0) as number;

      // Get cached data from React Query
      const cachedData = queryClient.getQueryData([
        hasMunu ? "munu-company-overview" : "food-company",
        row.original.buildingTenantId,
      ]);

      if (!cachedData) return [0, total, 0];
      if (!total) return [0, 0, 0];

      const { activeUsers, integratedUsersCount } = (cachedData || {}) as {
        activeUsers?: number | null;
        integratedUsersCount?: number | null;
      };

      let usersValue = Number(activeUsers || integratedUsersCount);
      if (isNaN(usersValue)) {
        usersValue = 0;
      }

      const percentage = getPercentage(usersValue, total);
      return [percentage, total, usersValue]; // [percentage value, total value, x value]
    };

    const [percentageA, totalA, xA] = getSortableTriple(rowA);
    const [percentageB, totalB, xB] = getSortableTriple(rowB);

    // Sort by percentage first
    if (percentageA !== percentageB) {
      return percentageA - percentageB;
    }

    // Sort by total second (if percentages are equal)
    if (totalA !== totalB) {
      return totalA - totalB;
    }

    // Sort by x value third if all else is equal
    return xA - xB;
  };
};

export const formatDateToTimezonedString = (date: Date | string): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ssXXX", {
    timeZone: "Europe/Oslo",
  });
};

export const isDateString = (value: string): value is DateString => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(value);
};

export const isTimeString = (
  value: string | null | undefined,
): value is TimeString => {
  const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
  return !!value && timeRegex.test(value);
};

export const isFromToDateFilter = (
  value: unknown,
): value is FromToDateFilterValue =>
  typeof value === "object" &&
  value !== null &&
  isDateString((value as FromToDateFilterValue).from ?? "") &&
  isDateString((value as FromToDateFilterValue).to ?? "");

export const dateToDateInputString = (date: Date): DateString => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}` as DateString;
};

export const firebaseTimestampToDateInputString = (
  timestamp: Timestamp,
): DateString => {
  return dateToDateInputString(firebaseTimestampToDate(timestamp));
};

export const dateInputStringToDate = (date: DateString): Date => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const dateToTimeInputString = (date: Date): TimeString => {
  return date.toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }) as TimeString;
};

export const firebaseTimestampToTimeInputString = (
  timestamp: Timestamp,
): TimeString => {
  return dateToTimeInputString(firebaseTimestampToDate(timestamp));
};

export const timeInputStringToDate = (
  time: TimeString,
  onDate?: Date | DateString,
): Date => {
  const [hours, minutes] = time.split(":").map(Number);
  const date = onDate
    ? onDate instanceof Date
      ? new Date(onDate)
      : dateInputStringToDate(onDate)
    : new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

export const subTime = (time: TimeString, minutes: number): TimeString => {
  const date = timeInputStringToDate(time);
  const newDate = subMinutes(date, minutes);
  return dateToTimeInputString(newDate);
};

export const addTime = (time: TimeString, minutes: number): TimeString => {
  const date = timeInputStringToDate(time);
  const newDate = addMinutes(date, minutes);
  return dateToTimeInputString(newDate);
};

export function roundToNearestHalfHour(date: Date): Date {
  const msInHalfHour = 30 * 60 * 1000;
  const roundedDate = new Date(
    Math.round(date.getTime() / msInHalfHour) * msInHalfHour,
  );
  return roundedDate;
}

export const calculateEndTime = (
  startTime: TimeString,
  duration: number,
  onDate?: Date | DateString,
): Date => {
  const [hours, minutes] = startTime.split(":");

  const startTimeAsDate = onDate ? new Date(onDate) : new Date();
  startTimeAsDate.setHours(Number(hours));
  startTimeAsDate.setMinutes(Number(minutes));

  return roundToNearestHalfHour(addMinutes(startTimeAsDate, duration * 60));
};

/**
 * Returns the first booked slot that overlaps the new slot.
 *
 * @param ignoreBuffers - Skip buffer slots while searching. Filtering after the fact isn't enough,
 * since a buffer found first would hide a real booking further along.
 */
export const getEventIfOverlapping = <T extends Event & { isBuffer?: boolean }>(
  bookedSlots: T[],
  newSlot: { start: Date; end: Date },
  { ignoreBuffers = false }: { ignoreBuffers?: boolean } = {},
): T | undefined => {
  return bookedSlots.find((slot) => {
    return (
      !(ignoreBuffers && slot.isBuffer) &&
      !!slot.end &&
      !!slot.start &&
      // New slot starts before the existing slot ends, but not exactly at the end
      isBefore(newSlot.start, slot.end) &&
      // New slot ends after the existing slot starts, but not exactly at the start
      isAfter(newSlot.end, slot.start)
    );
  });
};

/**
 * Collapses overlapping linked-room bookings into a single event.
 *
 * A linked event is not a booking of the room being viewed — it is that room being occupied by
 * a booking on a room it shares walls with. Two of them overlapping (both sub-rooms of a combined
 * room booked separately) would otherwise be laid out as side-by-side columns, which reads as two
 * separate bookings inside this room. Merging them into one event spanning the union of their times
 * says what is actually true: the room is unavailable for that whole stretch.
 *
 * Render-only — booking validation must keep running against the unmerged events.
 */
export const mergeOverlappingLinkedEvents = (
  events: CalendarEvent[],
  labelFor: (count: number, isUsersBooking: boolean) => string,
): CalendarEvent[] => {
  // An event missing either bound cannot be positioned, so it passes through as-is
  const isMergeable = (event: CalendarEvent) =>
    !!event.isFromLinked && !!event.start && !!event.end;

  const linked: CalendarEvent[] = [];
  const rest: CalendarEvent[] = [];
  events.forEach((event) => (isMergeable(event) ? linked : rest).push(event));
  if (linked.length < 2) return events;

  const sorted = linked.sort(
    (a, b) => (a.start as Date).getTime() - (b.start as Date).getTime(),
  );

  // Group into clusters of events that overlap, directly or through a chain of overlaps
  const clusters: CalendarEvent[][] = [];
  let cluster: CalendarEvent[] = [];
  let clusterEnd = sorted[0].end as Date;

  sorted.forEach((event) => {
    // Same half-open convention as getEventIfOverlapping: touching ends do not overlap
    if (!cluster.length || isBefore(event.start as Date, clusterEnd)) {
      cluster.push(event);
      if (isAfter(event.end as Date, clusterEnd))
        clusterEnd = event.end as Date;
      return;
    }

    clusters.push(cluster);
    cluster = [event];
    clusterEnd = event.end as Date;
  });
  if (cluster.length) clusters.push(cluster);

  const merged = clusters.flatMap((events) => {
    // A lone linked booking keeps its own title and id — nothing to collapse
    if (events.length < 2) return events;

    const start = events[0].start as Date;
    const end = events.reduce(
      (latest, event) =>
        isAfter(event.end as Date, latest) ? (event.end as Date) : latest,
      events[0].end as Date,
    );
    // Only claim the event as the user's when every booking behind it is theirs
    const isUsersBooking = events.every((event) => !!event.isUsersBooking);

    return [
      {
        id: `linked-merged-${start.toISOString()}-${end.toISOString()}`,
        title: labelFor(events.length, isUsersBooking),
        // Duplicates are kept: with hideEventTitles on, two bookings by the same company
        // share a title, and dropping one would contradict the count in the headline
        linkedTitles: events
          .map((event) => event.title)
          .filter(
            (title): title is string => typeof title === "string" && !!title,
          ),
        start,
        end,
        allDay: false,
        isFromLinked: true,
        isMergedLinked: true,
        isUsersBooking,
      } satisfies CalendarEvent,
    ];
  });

  return [...rest, ...merged];
};

export const isSlotInBlockedTime = (
  blockedSlots: BlockedTimeSlot[],
  newSlot: { start: Date; end: Date },
): boolean => {
  return blockedSlots.some((blocked) => {
    return (
      newSlot.start.getDay() === blocked.weekday &&
      // New slot starts before the blocked slot ends, but not exactly at the end
      isBefore(
        newSlot.start,
        timeInputStringToDate(blocked.to, newSlot.start),
      ) &&
      // New slot ends after the blocked slot starts, but not exactly at the start
      isAfter(newSlot.end, timeInputStringToDate(blocked.from, newSlot.end))
    );
  });
};

export const canCancelBooking = (
  bookingStart: Date,
  bookingEnd: Date,
  isBuildingAdmin: boolean,
): boolean => {
  const now = new Date();
  if (isBuildingAdmin) {
    return isBefore(now, bookingEnd);
  }
  return isBefore(now, subHours(bookingStart, 1));
};

export const canEditBooking = (bookingStart: Date): boolean => {
  return isBefore(new Date(), subHours(bookingStart, 1));
};

export const debounceAsync = <T extends (...args: any[]) => Promise<any>>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  let timeoutId: NodeJS.Timeout;
  let resolveList: ((value: ReturnType<T>) => void)[] = [];

  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    clearTimeout(timeoutId);

    return new Promise<ReturnType<T>>((resolve) => {
      resolveList.push(resolve);

      timeoutId = setTimeout(async () => {
        const result = await func(...args);
        resolveList.forEach((res) => res(result));
        resolveList = [];
      }, delay);
    });
  };
};

export function lowercaseFirstLetter(str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatAccessRoleName(str: string): string {
  return capitalizeFirstLetter(str.split("_").join(" "));
}

export function getTimestampOutOfString(timestamp: string): Timestamp {
  const date = new Date(timestamp);
  return {
    _seconds: Math.floor(date.getTime() / 1000),
    _nanoseconds: (date.getTime() % 1000) * 1e6,
  };
}

export const discountMultiplierToPercentage = (multiplier: number) =>
  `${Math.round((1 - multiplier) * 100)}%`;

export const percentageToDiscountMultiplier = (percentage: string) =>
  1 - parseFloat(percentage) / 100;

export function isInvalidPin(pin: string): {
  isInvalid: boolean;
  errorCodes?: string[];
} {
  const errors: string[] = [];

  // Rule 1: Ensure the PIN is exactly 4 digits
  if (pin.length !== 4) errors.push("INVALID_LENGTH");

  // Rule 2: No repeated digits (e.g., 1111)
  if (/^(.)\1{3,}$/.test(pin)) errors.push("REPEATED_DIGITS");

  // Rule 3: No consecutive numbers (ascending or descending)
  const ascending = "0123456789";
  const descending = "9876543210";
  if (ascending.includes(pin) || descending.includes(pin))
    errors.push("CONSECUTIVE_NUMBERS");

  // Rule 4: No straight line keypad patterns
  const keypadPatterns = ["2580", "0852"];
  if (keypadPatterns.includes(pin)) errors.push("KEYPAD_PATTERN");

  return { isInvalid: errors.length > 0, errorCodes: errors };
}

export function generateRandomPin(): string {
  const digits = new Uint32Array(4);
  for (;;) {
    crypto.getRandomValues(digits);
    const pin = Array.from(digits, (d) => d % 10).join("");
    if (!isInvalidPin(pin).isInvalid) return pin;
  }
}

export const scrollToFirstInvalidField = () => {
  const firstInvalid = document.querySelector('[data-invalid="true"]');
  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
    (firstInvalid as HTMLElement).focus?.();
  }
};

export function numberToRecipientGroup(choice: number) {
  if (choice === 1) return RecipientGroup.COMPANY_ADMINS;
  return RecipientGroup.ALL;
}
export function recipientGroupToChoiceNumber(group: RecipientGroup) {
  switch (group) {
    case RecipientGroup.ALL:
      return 0;
    case RecipientGroup.COMPANY_ADMINS:
      return 1;
  }
}

export function findMunuUser(
  user: User,
  munuCompanyUsers: MunuUserData[],
  /**
   * Optional physical access card number to match against Munu users.
   * - If provided, it will lead to a more secure match.
   * - If not provided, the function will only match by email or phone number.
   */
  physicalAccessCardNumber?: string | null,
): MunuUserData | undefined {
  let fallbackMatch: MunuUserData | undefined;

  for (const munuUser of munuCompanyUsers) {
    // Priority 1: access card match wins immediately, regardless of array order.
    if (
      physicalAccessCardNumber &&
      munuUser.CardNumber === physicalAccessCardNumber
    ) {
      return munuUser;
    }

    // Priority 2: remember the first email/phone match, but keep scanning
    // in case a later record matches by card.
    if (!fallbackMatch) {
      let userPhone = user.phone ?? "";
      if (
        userPhone &&
        userPhone[0] === "+" &&
        munuUser.MobilePhone &&
        munuUser.MobilePhone.length > 0 &&
        munuUser.MobilePhone[0] !== "+"
      ) {
        userPhone = userPhone.slice(1);
      }
      if (
        munuUser.EmailAddress === user.email ||
        munuUser.MobilePhone === userPhone
      ) {
        if (!physicalAccessCardNumber) return munuUser;
        fallbackMatch = munuUser;
      }
    }
  }

  return fallbackMatch;
}
/**
 * Returns the selected items from a React Table instance in the current display (sort) order.
 *
 * This function retrieves all rows from the table's sorted row model,
 * filters for those that are selected, and returns their original data objects.
 *
 * @template T - The type of the table row data.
 * @param {ReactTable<T> | null} tableInstance - The table instance to extract selected items from.
 * @returns {T[]} An array of selected items in the current display order. Returns an empty array if the table instance is null.
 */
export function getSelectedItemsInDisplayOrder<T>(
  tableInstance: ReactTable<T> | null,
): T[] {
  if (!tableInstance) return [];

  return tableInstance.getSelectedRowModel().rows.map((row) => row.original);
}

export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export function capitalizeName(name: string): string {
  return name
    .trim()
    .split(/\s+/) // split by spaces (handles multiple middle names)
    .map((word) =>
      word
        .split("-") // handle hyphenated names
        .map(
          (part) => part.charAt(0).toUpperCase() + part.slice(1), // only capitalize first letter
        )
        .join("-"),
    )
    .join(" ");
}

export function isMobileUA() {
  return /Mobi|Android|iPhone|iP(hone|od|ad)/.test(navigator.userAgent);
}

export function isAppleDevice() {
  return /iPad|iPhone|iPod|Macintosh|Mac OS X/.test(navigator.userAgent);
}

export async function processInBatches<T>(
  items: T[],
  batchSize: number,
  fn: (item: T) => Promise<any>,
): Promise<PromiseSettledResult<any>[]> {
  const results: PromiseSettledResult<any>[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(fn));
    results.push(...settled);
  }
  return results;
}

export const SNACK_ORDER_PRINT_STYLES = `
  @page {
    size: A4;
    margin: 15mm;
    padding: 0 7mm;
  }
  html, body {
    height: auto !important;
    overflow: visible !important;
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background-color: #fff !important;
  }
`;

export const ACCESS_CARD_PRINT_STYLES = `
  @page { 
    size: 210px 336px; 
    margin: 0; 
  }
  body, html { 
    margin: 0; 
    padding: 0; 
    background: white !important; 
    overflow: initial !important; 
  }
`;

export const foodDealLimitLabelStyles = (
  usedByCount: number | undefined,
  percentage?: number,
): CSSProperties => ({
  fontWeight: "bold",
  fontSize: "var(--wlcm-text-sm)",
  paddingLeft: "var(--wlcm-spacing-xs)",
  color:
    percentage && percentage > 100
      ? "var(--wlcm-color-danger-red)"
      : !usedByCount || usedByCount <= 0
        ? "var(--wlcm-color-dark-inactive-grey)"
        : "var(--wlcm-color-success-green)",
});

export const calculateLunchPrice = (
  basePrice: number,
  subsidyPercentage?: number,
) => {
  return (
    Math.round(basePrice * (1 - (subsidyPercentage ?? 0) / 100) * 100) / 100
  ); // rounded to nearest 2 decimal points
};

export const calculateLunchSubsidyPercentage = (
  basePrice: number,
  lunchPrice: number,
) => {
  if (basePrice <= 0) return 0;
  return Math.min(
    Math.max(Math.round(((basePrice - lunchPrice) / basePrice) * 100), 0),
    100,
  ); // only allows whole, positive percentage between 0% and 100%
};

export const calculateLunchSubsidyAmount = (
  basePrice: number,
  subsidyPercentage?: number,
) => {
  return basePrice * ((subsidyPercentage ?? 0) / 100);
};

export const joinWithAnd = (
  items: (ReactNode | undefined | null | false)[],
  andWord?: ReactNode,
): ReactNode => {
  const filteredItems = items.filter(
    (item) =>
      item !== undefined && item !== null && item !== false && item !== "",
  );
  if (!filteredItems.length) return "";
  if (filteredItems.length === 1) return filteredItems[0];

  const isAllStringsOrNumbers =
    filteredItems.every(
      (item) => typeof item === "string" || typeof item === "number",
    ) &&
    (andWord === undefined ||
      typeof andWord === "string" ||
      typeof andWord === "number");

  if (isAllStringsOrNumbers) {
    const lastItem = filteredItems[filteredItems.length - 1];
    const otherItems = filteredItems.slice(0, -1);
    return `${otherItems.join(", ")} ${andWord ?? "&"} ${lastItem}`;
  }

  return filteredItems.map((item, index) => {
    const isLast = index === filteredItems.length - 1;
    const isSecondToLast = index === filteredItems.length - 2;

    let separator: ReactNode = null;
    if (isSecondToLast) {
      separator = [" ", andWord ?? "&", " "];
    } else if (!isLast) {
      separator = ", "; // Space added after comma
    }

    return React.createElement(React.Fragment, { key: index }, item, separator);
  });
};

export const calculateNetPrice = (
  grossPrice: number,
  taxRatePercent: number,
) => {
  const taxRateDecimal = taxRatePercent / 100;
  const netPrice = grossPrice / (1 + taxRateDecimal);
  const netPriceRounded = Math.round(netPrice * 100) / 100;
  return {
    netPrice: netPriceRounded,
    vatAmount: Math.max(
      0,
      Math.round((grossPrice - netPriceRounded) * 100) / 100,
    ),
  };
};

export const isSnackOrderPickupMethod = (t?: string) => t?.startsWith("pickup");
export const isSnackOrderDeliveryMethod = (t?: string) =>
  t?.startsWith("delivery");
export const isSnackOrderWithServiceType = (t?: string) =>
  t?.endsWith("_service");
export const isSnackOrderWithService = (t?: string) =>
  t?.endsWith("_with_service");

// For prices stored excluding VAT — adds VAT on top rather than carving it out.
export const calculateVatAmount = (
  netPrice: number,
  vatRatePercent: number,
) => {
  const vatAmount = Math.round(netPrice * (vatRatePercent / 100) * 100) / 100;
  return {
    vatAmount,
    grossPrice: Math.round((netPrice + vatAmount) * 100) / 100,
  };
};
